import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Save, Globe, Mail, Phone, MapPin, Facebook, Instagram, 
  Youtube, Twitter, Image, Palette, Settings, RefreshCw,
  CreditCard, MessageCircle, Link2, Shield, Key, AlertCircle,
  CheckCircle, ExternalLink, Copy, BarChart3
} from 'lucide-react';
import ImageUploader from '@/components/ImageUploader';
import { adminFetch } from '../../utils/adminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [integrations, setIntegrations] = useState({
    domain: { custom_domain: '', ssl_enabled: true },
    email: { provider: 'resend', api_key: '', sender_email: '', sender_name: 'RO Dekho' },
    payment: { provider: 'razorpay', key_id: '', key_secret: '', test_mode: true },
    whatsapp: { provider: 'aisensy', api_key: '', business_id: '', template_namespace: '' }
  });
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [imageGuidelines, setImageGuidelines] = useState({});
  const [testingIntegration, setTestingIntegration] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchThemes();
    fetchImageGuidelines();
    fetchIntegrations();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cms/settings`);
      if (res.ok) {
        setSettings(await res.json());
      }
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchThemes = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/cms/themes`);
      if (res.ok) setThemes(await res.json());
    } catch (error) {
      console.error('Error fetching themes:', error);
    }
  };

  const fetchImageGuidelines = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/upload/guidelines`);
      if (res.ok) setImageGuidelines(await res.json());
    } catch (error) {
      console.error('Error fetching guidelines:', error);
    }
  };

  const fetchIntegrations = async () => {
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/integrations`);
      if (res.ok) {
        const data = await res.json();
        setIntegrations(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error fetching integrations:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/cms/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });

      if (res.ok) {
        toast.success('Settings saved successfully');
        fetchSettings();
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveIntegrations = async (type) => {
    setSaving(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/integrations/${type}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(integrations[type])
      });

      if (res.ok) {
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} settings saved`);
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const testIntegration = async (type) => {
    setTestingIntegration(type);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/integrations/${type}/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(integrations[type])
      });

      const result = await res.json();
      if (res.ok && result.success) {
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} connection successful!`);
      } else {
        toast.error(result.message || `${type} connection failed`);
      }
    } catch (error) {
      toast.error(`Failed to test ${type} connection`);
    } finally {
      setTestingIntegration(null);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSocialChange = (platform, value) => {
    setSettings(prev => ({
      ...prev,
      social_links: { ...prev.social_links, [platform]: value }
    }));
  };

  const handleIntegrationChange = (type, field, value) => {
    setIntegrations(prev => ({
      ...prev,
      [type]: { ...prev[type], [field]: value }
    }));
  };

  const activateTheme = async (themeId) => {
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/cms/themes/${themeId}/activate?page=home`, {
        method: 'PUT'
      });
      
      if (res.ok) {
        toast.success('Theme activated');
        fetchThemes();
      } else {
        toast.error('Failed to activate theme');
      }
    } catch (error) {
      toast.error('Failed to activate theme');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'contact', label: 'Contact', icon: Phone },
    { id: 'social', label: 'Social Media', icon: Globe },
    { id: 'seo', label: 'SEO', icon: Globe },
    { id: 'domain', label: 'Domain', icon: Link2 },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'payment', label: 'Payment', icon: CreditCard },
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
    { id: 'adtracking', label: 'Ad Tracking', icon: BarChart3 },
    { id: 'themes', label: 'Themes', icon: Palette }
  ];

  return (
    <div data-testid="admin-settings-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Site Settings</h1>
          <p className="text-gray-600">Configure your website's global settings and integrations</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? <RefreshCw size={18} className="animate-spin" /> : <Save size={18} />}
          Save Settings
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm mb-6">
        <div className="flex border-b overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        {/* General Tab */}
        {activeTab === 'general' && settings && (
          <div className="space-y-6 max-w-2xl">
            <h3 className="text-lg font-medium text-gray-900 pb-2 border-b">General Information</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Site Name</label>
              <input
                type="text"
                value={settings.site_name || ''}
                onChange={(e) => handleChange('site_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tagline</label>
              <input
                type="text"
                value={settings.tagline || ''}
                onChange={(e) => handleChange('tagline', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <ImageUploader
              value={settings.logo || ''}
              onChange={(url) => handleChange('logo', url)}
              folder="general"
              type="logo"
              label="Site Logo (PNG with transparent background recommended)"
            />

            <ImageUploader
              value={settings.favicon || ''}
              onChange={(url) => handleChange('favicon', url)}
              folder="general"
              type="favicon"
              label="Favicon (32x32px recommended)"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Footer Copyright Text</label>
              <input
                type="text"
                value={settings.footer_text || ''}
                onChange={(e) => handleChange('footer_text', e.target.value)}
                placeholder="© 2024 Arpsan Lifestyle Technology Pvt Ltd. All rights reserved."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}

        {/* Contact Tab */}
        {activeTab === 'contact' && settings && (
          <div className="space-y-6 max-w-2xl">
            <h3 className="text-lg font-medium text-gray-900 pb-2 border-b">Contact Information</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Mail className="inline w-4 h-4 mr-1" />
                Contact Email
              </label>
              <input
                type="email"
                value={settings.contact_email || ''}
                onChange={(e) => handleChange('contact_email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Phone className="inline w-4 h-4 mr-1" />
                Contact Phone
              </label>
              <input
                type="tel"
                value={settings.contact_phone || ''}
                onChange={(e) => handleChange('contact_phone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                WhatsApp Number (with country code)
              </label>
              <input
                type="tel"
                value={settings.whatsapp_number || ''}
                onChange={(e) => handleChange('whatsapp_number', e.target.value)}
                placeholder="+919887068888"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <MapPin className="inline w-4 h-4 mr-1" />
                Address
              </label>
              <textarea
                value={settings.address || ''}
                onChange={(e) => handleChange('address', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}

        {/* Social Media Tab */}
        {activeTab === 'social' && settings && (
          <div className="space-y-6 max-w-2xl">
            <h3 className="text-lg font-medium text-gray-900 pb-2 border-b">Social Media Links</h3>
            
            {[
              { id: 'facebook', label: 'Facebook', icon: Facebook, placeholder: 'https://facebook.com/yourpage' },
              { id: 'instagram', label: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/yourhandle' },
              { id: 'youtube', label: 'YouTube', icon: Youtube, placeholder: 'https://youtube.com/@yourchannel' },
              { id: 'twitter', label: 'Twitter/X', icon: Twitter, placeholder: 'https://twitter.com/yourhandle' }
            ].map(social => (
              <div key={social.id}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <social.icon className="inline w-4 h-4 mr-1" />
                  {social.label}
                </label>
                <input
                  type="url"
                  value={settings.social_links?.[social.id] || ''}
                  onChange={(e) => handleSocialChange(social.id, e.target.value)}
                  placeholder={social.placeholder}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ))}
          </div>
        )}

        {/* SEO Tab */}
        {activeTab === 'seo' && settings && (
          <div className="space-y-6 max-w-2xl">
            <h3 className="text-lg font-medium text-gray-900 pb-2 border-b">SEO Settings</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Meta Title</label>
              <input
                type="text"
                value={settings.meta_title || ''}
                onChange={(e) => handleChange('meta_title', e.target.value)}
                maxLength={60}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">{(settings.meta_title || '').length}/60 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Meta Description</label>
              <textarea
                value={settings.meta_description || ''}
                onChange={(e) => handleChange('meta_description', e.target.value)}
                maxLength={160}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">{(settings.meta_description || '').length}/160 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Google Analytics 4 Measurement ID</label>
              <input
                type="text"
                value={settings.google_analytics_id || ''}
                onChange={(e) => handleChange('google_analytics_id', e.target.value)}
                placeholder="G-XXXXXXXXXX"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Paste from analytics.google.com. Page-view tracking activates automatically. Same field also lives in Ad Campaigns tab.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Google Search Console Verification</label>
              <input
                type="text"
                value={settings.google_site_verification || ''}
                onChange={(e) => handleChange('google_site_verification', e.target.value)}
                placeholder="paste only the content value (e.g. abcd1234XYZefgh...)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="gsc-verification-input"
              />
              <p className="text-xs text-gray-500 mt-1">From Search Console → "HTML tag" verification: paste only the <code className="bg-gray-100 px-1 rounded">content="..."</code> value. The meta tag will be injected automatically site-wide.</p>
            </div>
          </div>
        )}

        {/* Domain Tab */}
        {activeTab === 'domain' && (
          <div className="space-y-6 max-w-2xl">
            <div className="flex items-center gap-2 pb-2 border-b">
              <Link2 className="text-blue-600" size={20} />
              <h3 className="text-lg font-medium text-gray-900">Domain Configuration</h3>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-blue-600 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-blue-900">Current Domain</p>
                  <p className="text-blue-700 text-sm mt-1">
                    Your app is currently accessible at:
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <code className="bg-white px-3 py-1 rounded text-sm font-mono">
                      {window.location.origin}
                    </code>
                    <button 
                      onClick={() => copyToClipboard(window.location.origin)}
                      className="p-1 hover:bg-blue-100 rounded"
                    >
                      <Copy size={16} className="text-blue-600" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custom Domain
              </label>
              <input
                type="text"
                value={integrations.domain?.custom_domain || ''}
                onChange={(e) => handleIntegrationChange('domain', 'custom_domain', e.target.value)}
                placeholder="www.rodekho.in"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                After deployment, you can configure a custom domain through the Emergent dashboard.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">DNS Configuration</h4>
              <p className="text-sm text-gray-600 mb-3">
                To connect your custom domain, add these DNS records:
              </p>
              <div className="space-y-2 text-sm font-mono bg-white p-3 rounded border">
                <p><span className="text-gray-500">Type:</span> CNAME</p>
                <p><span className="text-gray-500">Name:</span> www</p>
                <p><span className="text-gray-500">Value:</span> your-app.emergent.host</p>
              </div>
            </div>
          </div>
        )}

        {/* Email Tab */}
        {activeTab === 'email' && (
          <div className="space-y-6 max-w-2xl">
            <div className="flex items-center justify-between pb-2 border-b">
              <div className="flex items-center gap-2">
                <Mail className="text-green-600" size={20} />
                <h3 className="text-lg font-medium text-gray-900">Email Configuration (Resend)</h3>
              </div>
              <a 
                href="https://resend.com/api-keys" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
              >
                Get API Key <ExternalLink size={14} />
              </a>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-amber-600 mt-0.5" size={20} />
                <div className="text-sm">
                  <p className="font-medium text-amber-900">Setup Instructions</p>
                  <ol className="text-amber-700 mt-2 space-y-1 list-decimal list-inside">
                    <li>Sign up at <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="underline">resend.com</a></li>
                    <li>Create an API key from the dashboard</li>
                    <li>Verify your sending domain for better deliverability</li>
                    <li>Enter the API key below</li>
                  </ol>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Key className="inline w-4 h-4 mr-1" />
                Resend API Key
              </label>
              <input
                type="password"
                value={integrations.email?.api_key || ''}
                onChange={(e) => handleIntegrationChange('email', 'api_key', e.target.value)}
                placeholder="re_xxxxxxxx..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sender Email</label>
              <input
                type="email"
                value={integrations.email?.sender_email || ''}
                onChange={(e) => handleIntegrationChange('email', 'sender_email', e.target.value)}
                placeholder="noreply@rodekho.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sender Name</label>
              <input
                type="text"
                value={integrations.email?.sender_name || ''}
                onChange={(e) => handleIntegrationChange('email', 'sender_name', e.target.value)}
                placeholder="RO Dekho"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => handleSaveIntegrations('email')}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <Save size={18} />
                Save Email Settings
              </button>
              <button
                onClick={() => testIntegration('email')}
                disabled={testingIntegration === 'email' || !integrations.email?.api_key}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                {testingIntegration === 'email' ? (
                  <RefreshCw size={18} className="animate-spin" />
                ) : (
                  <CheckCircle size={18} />
                )}
                Test Connection
              </button>
            </div>
          </div>
        )}

        {/* Payment Tab */}
        {activeTab === 'payment' && (
          <div className="space-y-6 max-w-2xl">
            <div className="flex items-center justify-between pb-2 border-b">
              <div className="flex items-center gap-2">
                <CreditCard className="text-purple-600" size={20} />
                <h3 className="text-lg font-medium text-gray-900">Payment Gateway (Razorpay)</h3>
              </div>
              <a 
                href="https://dashboard.razorpay.com/app/keys" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
              >
                Get API Keys <ExternalLink size={14} />
              </a>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Shield className="text-purple-600 mt-0.5" size={20} />
                <div className="text-sm">
                  <p className="font-medium text-purple-900">Setup Instructions</p>
                  <ol className="text-purple-700 mt-2 space-y-1 list-decimal list-inside">
                    <li>Login to <a href="https://dashboard.razorpay.com" target="_blank" rel="noopener noreferrer" className="underline">Razorpay Dashboard</a></li>
                    <li>Go to Settings → API Keys</li>
                    <li>Generate Key ID and Key Secret</li>
                    <li>Use Test keys for testing, Live keys for production</li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={integrations.payment?.test_mode}
                  onChange={(e) => handleIntegrationChange('payment', 'test_mode', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-purple-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
              </label>
              <span className="text-sm font-medium text-gray-700">
                {integrations.payment?.test_mode ? 'Test Mode (Use test keys)' : 'Live Mode (Use live keys)'}
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Key className="inline w-4 h-4 mr-1" />
                Razorpay Key ID
              </label>
              <input
                type="text"
                value={integrations.payment?.key_id || ''}
                onChange={(e) => handleIntegrationChange('payment', 'key_id', e.target.value)}
                placeholder="rzp_test_xxxxxxxx or rzp_live_xxxxxxxx"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Key className="inline w-4 h-4 mr-1" />
                Razorpay Key Secret
              </label>
              <input
                type="password"
                value={integrations.payment?.key_secret || ''}
                onChange={(e) => handleIntegrationChange('payment', 'key_secret', e.target.value)}
                placeholder="Enter your key secret"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => handleSaveIntegrations('payment')}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                <Save size={18} />
                Save Payment Settings
              </button>
              <button
                onClick={() => testIntegration('payment')}
                disabled={testingIntegration === 'payment' || !integrations.payment?.key_id}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                {testingIntegration === 'payment' ? (
                  <RefreshCw size={18} className="animate-spin" />
                ) : (
                  <CheckCircle size={18} />
                )}
                Test Connection
              </button>
            </div>
          </div>
        )}

        {/* WhatsApp Tab */}
        {activeTab === 'whatsapp' && (
          <div className="space-y-6 max-w-2xl">
            <div className="flex items-center justify-between pb-2 border-b">
              <div className="flex items-center gap-2">
                <MessageCircle className="text-green-600" size={20} />
                <h3 className="text-lg font-medium text-gray-900">WhatsApp Notifications (AiSensy)</h3>
              </div>
              <a 
                href="https://app.aisensy.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
              >
                AiSensy Dashboard <ExternalLink size={14} />
              </a>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <MessageCircle className="text-green-600 mt-0.5" size={20} />
                <div className="text-sm">
                  <p className="font-medium text-green-900">Setup Instructions</p>
                  <ol className="text-green-700 mt-2 space-y-1 list-decimal list-inside">
                    <li>Sign up at <a href="https://aisensy.com" target="_blank" rel="noopener noreferrer" className="underline">aisensy.com</a></li>
                    <li>Connect your WhatsApp Business number</li>
                    <li>Create message templates for order notifications</li>
                    <li>Get your API key from the dashboard</li>
                  </ol>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Key className="inline w-4 h-4 mr-1" />
                AiSensy API Key
              </label>
              <input
                type="password"
                value={integrations.whatsapp?.api_key || ''}
                onChange={(e) => handleIntegrationChange('whatsapp', 'api_key', e.target.value)}
                placeholder="Enter your API key"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business ID</label>
              <input
                type="text"
                value={integrations.whatsapp?.business_id || ''}
                onChange={(e) => handleIntegrationChange('whatsapp', 'business_id', e.target.value)}
                placeholder="Your WhatsApp Business ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Template Namespace</label>
              <input
                type="text"
                value={integrations.whatsapp?.template_namespace || ''}
                onChange={(e) => handleIntegrationChange('whatsapp', 'template_namespace', e.target.value)}
                placeholder="Message template namespace"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => handleSaveIntegrations('whatsapp')}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <Save size={18} />
                Save WhatsApp Settings
              </button>
              <button
                onClick={() => testIntegration('whatsapp')}
                disabled={testingIntegration === 'whatsapp' || !integrations.whatsapp?.api_key}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                {testingIntegration === 'whatsapp' ? (
                  <RefreshCw size={18} className="animate-spin" />
                ) : (
                  <CheckCircle size={18} />
                )}
                Test Connection
              </button>
            </div>
          </div>
        )}

        {/* Ad Tracking Tab */}
        {activeTab === 'adtracking' && settings && (
          <div className="space-y-6 max-w-2xl">
            <div className="flex items-center gap-2 pb-2 border-b">
              <BarChart3 className="text-blue-600" size={20} />
              <h3 className="text-lg font-medium text-gray-900">Ad Campaign Tracking</h3>
            </div>
            <p className="text-sm text-gray-600">Configure your Google Ads and Meta (Facebook) Pixel tracking codes for performance marketing. These will automatically fire conversion events on page views, add to cart, purchases, and service bookings.</p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-2 text-sm">Google Ads Conversion Tracking</h4>
              <p className="text-xs text-blue-700 mb-3">Enter your Google Ads Conversion ID (starts with AW-). Get it from Google Ads &gt; Tools &gt; Conversions.</p>
              <label className="block text-sm font-medium text-gray-700 mb-1">Google Ads Conversion ID</label>
              <input
                type="text"
                value={settings.google_ads_id || ''}
                onChange={(e) => handleChange('google_ads_id', e.target.value)}
                placeholder="AW-XXXXXXXXX"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="google-ads-id-input"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-2 text-sm">Google Analytics 4 (GA4)</h4>
              <p className="text-xs text-blue-700 mb-3">Enter your GA4 Measurement ID (starts with G-). Get it from Google Analytics &gt; Admin &gt; Data Streams.</p>
              <label className="block text-sm font-medium text-gray-700 mb-1">GA4 Measurement ID</label>
              <input
                type="text"
                value={settings.google_analytics_id || ''}
                onChange={(e) => handleChange('google_analytics_id', e.target.value)}
                placeholder="G-XXXXXXXXXX"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="ga4-id-input"
              />
            </div>

            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h4 className="font-semibold text-indigo-800 mb-2 text-sm">Meta (Facebook) Pixel</h4>
              <p className="text-xs text-indigo-700 mb-3">Enter your Meta Pixel ID (numeric). Get it from Meta Events Manager &gt; Data Sources &gt; Pixels.</p>
              <label className="block text-sm font-medium text-gray-700 mb-1">Meta Pixel ID</label>
              <input
                type="text"
                value={settings.meta_pixel_id || ''}
                onChange={(e) => handleChange('meta_pixel_id', e.target.value)}
                placeholder="XXXXXXXXXXXXXXX"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="meta-pixel-id-input"
              />
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2 text-sm">Tracked Conversion Events</h4>
              <p className="text-xs text-gray-600 mb-3">These events are automatically tracked when the pixel IDs are configured:</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { event: 'PageView', desc: 'Every page visit' },
                  { event: 'ViewContent', desc: 'Product page views' },
                  { event: 'AddToCart', desc: 'Add to cart clicks' },
                  { event: 'InitiateCheckout', desc: 'Checkout page visits' },
                  { event: 'Purchase', desc: 'Successful orders' },
                  { event: 'Lead', desc: 'Service booking submissions' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <CheckCircle size={14} className="text-green-600 shrink-0" />
                    <span><strong>{item.event}</strong> - {item.desc}</span>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
              data-testid="save-tracking-btn"
            >
              <Save size={16} />
              {saving ? 'Saving...' : 'Save Tracking Settings'}
            </button>
          </div>
        )}

        {/* Themes Tab */}
        {activeTab === 'themes' && (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 pb-2 border-b">Color Themes</h3>
            
            <div className="grid md:grid-cols-3 gap-4">
              {themes.map(theme => (
                <div
                  key={theme.theme_id}
                  className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                    theme.is_active ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => activateTheme(theme.theme_id)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">{theme.name}</h4>
                    {theme.is_active && (
                      <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">Active</span>
                    )}
                  </div>
                  
                  {/* Color Preview */}
                  <div className="flex gap-2 mb-3">
                    <div
                      className="w-8 h-8 rounded-full border"
                      style={{ backgroundColor: theme.settings?.primary_color }}
                      title="Primary"
                    />
                    <div
                      className="w-8 h-8 rounded-full border"
                      style={{ backgroundColor: theme.settings?.secondary_color }}
                      title="Secondary"
                    />
                    <div
                      className="w-8 h-8 rounded-full border"
                      style={{ backgroundColor: theme.settings?.accent_color }}
                      title="Accent"
                    />
                    <div
                      className="w-8 h-8 rounded-full border"
                      style={{ backgroundColor: theme.settings?.background }}
                      title="Background"
                    />
                  </div>
                  
                  {/* Preview Bar */}
                  <div
                    className="h-2 rounded-full"
                    style={{
                      background: `linear-gradient(to right, ${theme.settings?.primary_color}, ${theme.settings?.secondary_color})`
                    }}
                  />
                </div>
              ))}
            </div>
            
            <p className="text-sm text-gray-500">
              Click on a theme to activate it. Theme colors will be applied to your website.
            </p>
          </div>
        )}
      </div>

      {/* Image Guidelines Reference */}
      {activeTab === 'general' && (
        <div className="bg-gray-50 rounded-xl p-4 mt-6">
          <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
            <Image size={18} />
            Image Size Reference Guide
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            {Object.entries(imageGuidelines).map(([key, value]) => (
              <div key={key} className="bg-white rounded-lg p-2">
                <p className="font-medium text-gray-900 capitalize">{key}</p>
                <p className="text-xs text-gray-500">{value.width}x{value.height}px</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminSettings;
